<template>
  <Teleport to="body">
  <div
    v-if="open"
    class="label-export-overlay"
    @click.self="onBackdropClick"
  >
    <div class="label-export-card" @click.stop>
      <!-- Notice: nothing to export -->
      <template v-if="showNotice">
        <h3 class="mb-2 text-base font-semibold">
          <i class="mr-1 fa fa-tags"></i> Eksport etikete
        </h3>
        <p class="mb-4 text-sm text-slate-700">{{ noticeText }}</p>
        <div class="flex justify-end">
          <button type="button" class="px-3 py-1 text-sm text-white bg-teal-600 rounded" @click="onClose">
            OK
          </button>
        </div>
      </template>

      <!-- Export settings -->
      <template v-else>
        <h3 class="mb-2 text-base font-semibold"><i class="mr-1 fa fa-file-pdf"></i> {{ title }}</h3>
        <p v-if="subtitle" class="mb-2 text-sm text-gray-600" v-html="subtitle"></p>

        <div v-if="previewLoading" class="py-6 text-sm text-center text-gray-500">
          <i class="fa fa-spinner fa-spin mr-1"></i> Duke llogaritur eksportin...
        </div>

        <template v-else>
          <div v-if="previewError" class="p-3 mb-3 text-sm text-red-700 rounded bg-red-50">
            {{ previewError }}
          </div>

          <template v-if="preview">
            <div class="p-3 mb-3 text-sm rounded bg-slate-50 border border-slate-200">
              <p class="font-semibold text-slate-800">
                {{ preview.total_labels }} {{ preview.total_labels === 1 ? "produkt" : "produkte" }} do të eksportohen si PDF
              </p>
              <p v-if="sizeBreakdown" class="mt-1 text-slate-600">{{ sizeBreakdown }}</p>
              <p class="mt-2 text-xs text-slate-500">Formati: PDF (A4 — landscape për A5/A7, portrait për A6)</p>
            </div>

            <div v-if="sampleLabels.length" class="mb-3">
              <p class="mb-2 text-sm font-medium text-slate-700">Preview e Etiketës</p>
              <div class="label-preview-list">
                <div
                  v-for="(label, idx) in sampleLabels"
                  :key="'sample-' + idx"
                  class="label-preview-card"
                >
                  <div class="label-preview-size" v-if="label.paper_size">{{ String(label.paper_size).toUpperCase() }}</div>
                  <div class="label-preview-title">{{ label.title || "—" }}</div>
                  <div class="label-preview-subtitle" v-if="label.subtitle">{{ label.subtitle }}</div>
                  <table v-if="label.rows && label.rows.length" class="label-preview-rows">
                    <tr v-for="(row, rIdx) in label.rows" :key="'row-' + idx + '-' + rIdx">
                      <td>{{ row.attribute_name }}</td>
                      <td>{{ row.attribute_value }}</td>
                    </tr>
                  </table>
                  <div class="label-preview-footer">
                    <span v-if="label.price" class="label-preview-price">{{ label.price }}</span>
                    <span v-if="label.warranty" class="label-preview-warranty">{{ label.warranty }}</span>
                  </div>
                </div>
              </div>
              <p v-if="preview.total_labels > sampleLabels.length" class="mt-1 text-xs text-slate-500">
                Duke shfaqur {{ sampleLabels.length }} nga {{ preview.total_labels }} etiketa
              </p>
            </div>

            <p class="mb-2 text-sm text-gray-600">
              Zgjidhni sa etiketa të vendosen për faqe A4 sipas madhësisë së etiketës.
            </p>

            <div v-for="opt in sizeOptions" :key="opt.size" class="mb-3">
              <div class="flex items-center justify-between mb-1">
                <label class="text-sm font-medium">
                  {{ opt.label }}
                  <span class="font-normal text-gray-400">({{ opt.count }} etiketa · maks. {{ opt.max }} / faqe)</span>
                </label>
                <input
                  v-model.number="perPage[opt.size]"
                  type="number"
                  :min="1"
                  :max="opt.max"
                  class="w-24 px-2 py-1 text-sm border rounded"
                  @change="clamp(opt.size)"
                />
              </div>
              <p class="text-xs text-gray-500">≈ {{ estimatedPages(opt) }} faqe A4</p>
            </div>
          </template>
        </template>

        <div class="flex justify-end gap-2 mt-4">
          <button type="button" class="px-3 py-1 text-sm border rounded" @click="onClose">Anulo</button>
          <button
            type="button"
            class="px-3 py-1 text-sm text-white bg-teal-600 rounded"
            :class="{ 'opacity-50 pointer-events-none': loading || previewLoading || !canExport }"
            @click="confirm"
          >
            <span v-if="loading"><i class="fa fa-spinner fa-spin mr-1"></i> Duke shkarkuar...</span>
            <span v-else><i class="mr-1 fa fa-download"></i> Shkarko PDF</span>
          </button>
        </div>
      </template>
    </div>
  </div>
  </Teleport>
</template>

<script>
import ProductsServices from "@/services/Product";

const SIZE_LABELS = { a5: "A5", a6: "A6", a7: "A7" };

/** Singular / plural copy when nothing can be exported. */
export function labelNoExportMessage(count = 1) {
  return count === 1
    ? "Ky produkt nuk duhet te kete etikete"
    : "Keto produkte nuk duhet te kene etikete";
}

export const LABEL_NO_EXPORT_MESSAGE = labelNoExportMessage(2);

function formatApiError(error, fallback) {
  const data = error?.response?.data;
  if (!data) return error?.message || fallback;

  const fieldErrors = data.errors;
  if (fieldErrors && typeof fieldErrors === "object") {
    const first = Object.values(fieldErrors).flat().find(Boolean);
    if (first) return String(first);
  }

  if (data.message && data.message !== "The given data was invalid.") {
    return data.message;
  }

  return fallback;
}

export default {
  name: "LabelExportModal",
  props: {
    open: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    title: { type: String, default: "Eksporto etiketat (PDF)" },
    subtitle: { type: String, default: "" },
    /** 'export' = normal export UI; 'notice' = only "nuk ka etiketë" popup */
    mode: { type: String, default: "export" },
    /** Products considered when showing the notice (for ky / këto). */
    noticeProductCount: { type: Number, default: 1 },
    exportContext: {
      type: Object,
      default: () => ({
        product_guids: [],
        category_guid: null,
        related_entity: "products",
      }),
    },
  },
  emits: ["close", "confirm"],
  data() {
    return {
      preview: null,
      previewLoading: false,
      previewError: null,
      perPage: {},
      internalNoticeCount: null,
    };
  },
  computed: {
    showNotice() {
      return this.mode === "notice" || this.internalNoticeCount !== null;
    },
    noticeText() {
      const count =
        this.internalNoticeCount !== null
          ? this.internalNoticeCount
          : Math.max(1, this.noticeProductCount || 1);
      return labelNoExportMessage(count);
    },
    includeDescendants() {
      return !!this.exportContext?.category_guid;
    },
    sizeOptions() {
      if (!this.preview?.by_size) return [];
      return Object.entries(this.preview.by_size).map(([size, meta]) => ({
        size,
        label: SIZE_LABELS[size] || size.toUpperCase(),
        count: meta.count,
        max: meta.max_per_page,
        default: meta.default_per_page,
      }));
    },
    sizeBreakdown() {
      if (!this.preview?.by_size) return "";
      return Object.entries(this.preview.by_size)
        .map(([size, meta]) => `${SIZE_LABELS[size] || size.toUpperCase()}: ${meta.count}`)
        .join(" · ");
    },
    canExport() {
      return this.preview && this.preview.total_labels > 0 && !this.previewError;
    },
    sampleLabels() {
      const samples = this.preview?.sample_labels;
      return Array.isArray(samples) ? samples : [];
    },
  },
  watch: {
    open(value) {
      if (value) {
        this.internalNoticeCount = null;
        if (this.mode === "notice") return;
        this.loadPreview();
      } else {
        this.preview = null;
        this.previewError = null;
        this.perPage = {};
        this.internalNoticeCount = null;
      }
    },
    exportContext: {
      deep: true,
      handler() {
        if (this.open && this.mode !== "notice" && this.internalNoticeCount === null) {
          this.loadPreview();
        }
      },
    },
  },
  methods: {
    async loadPreview() {
      this.previewLoading = true;
      this.previewError = null;
      try {
        const ctx = this.exportContext || {};
        const hasGuids = (ctx.product_guids || []).length > 0;
        const hasCategory = !!ctx.category_guid;
        if (!hasGuids && !hasCategory) {
          this.previewError = "Nuk ka produkte ose kategori për eksport.";
          this.preview = null;
          return;
        }
        const data = await ProductsServices.previewLabelExport({
          product_guids: ctx.product_guids || [],
          category_guid: ctx.category_guid || null,
          related_entity: ctx.related_entity || "products",
          include_descendants: this.includeDescendants,
        });
        this.preview = data;
        if (data.total_labels === 0) {
          this.internalNoticeCount = Math.max(
            1,
            (ctx.product_guids || []).length || data.total_products || 1
          );
          return;
        }
        this.initPerPageFromPreview(data);
      } catch (e) {
        this.previewError = formatApiError(e, "Gabim gjatë ngarkimit të preview.");
        this.preview = null;
      } finally {
        this.previewLoading = false;
      }
    },
    initPerPageFromPreview(data) {
      const next = {};
      Object.entries(data?.by_size || {}).forEach(([size, meta]) => {
        next[size] = meta.default_per_page ?? meta.max_per_page ?? 1;
      });
      this.perPage = next;
    },
    estimatedPages(opt) {
      const per = parseInt(this.perPage[opt.size], 10) || opt.default || 1;
      return Math.max(1, Math.ceil(opt.count / per));
    },
    onClose() {
      if (this.loading) return;
      this.$emit("close");
    },
    onBackdropClick() {
      // Notice: must press OK — backdrop does not dismiss.
      if (this.showNotice) return;
      this.onClose();
    },
    clamp(size) {
      const opt = this.sizeOptions.find((o) => o.size === size);
      if (!opt) return;
      let value = parseInt(this.perPage[size], 10);
      if (isNaN(value) || value < 1) value = 1;
      if (value > opt.max) value = opt.max;
      this.perPage[size] = value;
    },
    confirm() {
      if (!this.canExport) return;
      this.sizeOptions.forEach((o) => this.clamp(o.size));
      this.$emit("confirm", {
        per_page: { ...this.perPage },
        include_descendants: this.includeDescendants,
      });
    },
  },
};
</script>

<style scoped>
.label-export-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.4);
}
.label-export-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 42rem;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1rem;
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
}
.label-preview-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 280px;
  overflow-y: auto;
}
.label-preview-card {
  position: relative;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 0.75rem 0.9rem;
  background: #fff;
  box-shadow: inset 0 0 0 1px #f1f5f9;
}
.label-preview-size {
  position: absolute;
  top: 0.4rem;
  right: 0.5rem;
  font-size: 0.65rem;
  color: #64748b;
  font-weight: 600;
}
.label-preview-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
  padding-right: 2rem;
  line-height: 1.25;
}
.label-preview-subtitle {
  margin-top: 0.15rem;
  font-size: 0.8rem;
  color: #334155;
}
.label-preview-rows {
  width: 100%;
  margin-top: 0.5rem;
  border-collapse: collapse;
  font-size: 0.72rem;
}
.label-preview-rows td {
  padding: 0.15rem 0.25rem;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
}
.label-preview-rows td:first-child {
  width: 42%;
  color: #64748b;
}
.label-preview-rows td:last-child {
  color: #0f172a;
  font-weight: 600;
}
.label-preview-footer {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 0.5rem;
  gap: 0.5rem;
}
.label-preview-price {
  font-size: 1rem;
  font-weight: 800;
  color: #0f172a;
}
.label-preview-warranty {
  font-size: 0.7rem;
  color: #64748b;
}
</style>
